import { getOfferedPrice } from '@bettermode/domain/billing/plan-offer'
import {
  BillingPriceInterval,
  type BillingProduct,
} from '@tribeplatform/gql-client/types'

/**
 * The price the plans page sells at an interval: the default one, and only that. A plan
 * whose only price at an interval is non-default is sales-led there and gets no offer.
 */
export const getOfferedPriceByInterval = (
  product: Pick<BillingProduct, 'prices'>,
  interval: BillingPriceInterval,
) => getOfferedPrice(product.prices, interval)

/**
 * The customer's own plan price, whatever its `default` flag. A sales-made subscription
 * can sit on a non-default price; its card must still read as the current plan.
 */
export const getCurrentPrice = (
  product: Pick<BillingProduct, 'prices'>,
  planPriceId: string | null | undefined,
) =>
  planPriceId
    ? product.prices.find(price => price.id === planPriceId)
    : undefined

export const getPlanPricing = (
  product: Pick<BillingProduct, 'prices'>,
  interval: BillingPriceInterval,
) => {
  const price = getOfferedPriceByInterval(product, interval)
  const monthlyPrice = getOfferedPriceByInterval(
    product,
    BillingPriceInterval.Month,
  )

  let monthlyAmount = 0

  if (price) {
    monthlyAmount =
      price.interval === BillingPriceInterval.Month
        ? price.amount / 100
        : price.amount / 100 / 12
  }

  const yearlyTotal = price ? monthlyAmount * 12 : null

  const monthlyTotal = monthlyPrice ? (monthlyPrice.amount / 100) * 12 : null
  const savingsPercent =
    monthlyTotal && price && price.interval === BillingPriceInterval.Year
      ? Math.round(((monthlyTotal - price.amount / 100) / monthlyTotal) * 100)
      : null

  return {
    price,
    monthlyPrice,
    monthlyAmount,
    yearlyTotal,
    savingsPercent,
  }
}
